import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../lib/api';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Select from '../ui/Select';
import Badge from '../ui/Badge';
import type { BulkLinkResult } from '../../lib/googleWorkspace';

interface JobOption {
  id: string;
  title: string;
}

interface BulkLinkCampaignsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

export function BulkLinkCampaignsModal({ isOpen, onClose, onComplete }: BulkLinkCampaignsModalProps) {
  const [jobs, setJobs] = useState<JobOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sourceJobId, setSourceJobId] = useState('');
  const [targetJobIds, setTargetJobIds] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<BulkLinkResult['results'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setResults(null);
    setError(null);
    setSourceJobId('');
    setTargetJobIds(new Set());

    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get<Array<{ id: string; title: string }>>('/jobs');
        setJobs(res.data.map((j) => ({ id: j.id, title: j.title })));
      } catch {
        setError('Failed to load campaigns');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [isOpen]);

  const toggleTarget = (id: string) => {
    if (id === sourceJobId) return;
    setTargetJobIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!sourceJobId || targetJobIds.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post<BulkLinkResult>('/google-workspace/jobs/bulk-link', {
        sourceJobId,
        targetJobIds: [...targetJobIds],
      });
      setResults(res.data.results);
      onComplete?.();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Bulk link failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk link Google files to campaigns"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {results ? 'Close' : 'Cancel'}
          </Button>
          {!results && (
            <Button
              onClick={() => void handleSubmit()}
              disabled={submitting || !sourceJobId || targetJobIds.size === 0}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply links'}
            </Button>
          )}
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : results ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            Linked Google files from the source campaign to {results.length} campaign
            {results.length === 1 ? '' : 's'}.
          </p>
          <ul className="text-sm space-y-2">
            {results.map((r) => (
              <li key={r.jobId} className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{jobs.find((j) => j.id === r.jobId)?.title || r.jobId}</span>
                {r.linked.map((t) => (
                  <Badge key={t} variant="outline">
                    {t}
                  </Badge>
                ))}
                {r.error && <span className="text-red-600">{r.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Copy linked Google Doc, Slides, and folder URLs from one campaign to others. Per-KOL
            storyboards are not copied.
          </p>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div>
            <label className="block text-sm font-medium mb-1">Source campaign</label>
            <Select value={sourceJobId} onChange={(e) => setSourceJobId(e.target.value)}>
              <option value="">Select campaign with linked Google files</option>
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.title}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Target campaigns</label>
            <div className="max-h-48 overflow-y-auto rounded-lg border divide-y">
              {jobs
                .filter((j) => j.id !== sourceJobId)
                .map((j) => (
                  <label
                    key={j.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={targetJobIds.has(j.id)}
                      disabled={!sourceJobId}
                      onChange={() => toggleTarget(j.id)}
                    />
                    {j.title}
                  </label>
                ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
